import { useCallback, useEffect, useState } from "react";
import { getAuthHeaders, useAuth } from "../contexts/AuthContext";
import type { GameState, Match, Player } from "../types";

// Transform snake_case from API to camelCase for frontend
interface ApiPlayer {
	id: string;
	name: string;
	elo: number;
	wins: number;
	losses: number;
}

interface ApiMatch {
	id: string;
	winner_id: string;
	loser_id: string;
	winner_elo_before: number;
	loser_elo_before: number;
	winner_elo_after: number;
	loser_elo_after: number;
	timestamp: number;
}

function transformPlayer(p: ApiPlayer): Player {
	return p; // Same shape, no transformation needed
}

function transformMatch(m: ApiMatch): Match {
	return {
		id: m.id,
		winnerId: m.winner_id,
		loserId: m.loser_id,
		winnerEloBefore: m.winner_elo_before,
		loserEloBefore: m.loser_elo_before,
		winnerEloAfter: m.winner_elo_after,
		loserEloAfter: m.loser_elo_after,
		timestamp: m.timestamp,
	};
}

export function useEloTracker() {
	const { token, isAuthenticated } = useAuth();
	const [state, setState] = useState<GameState>({ players: [], matches: [] });
	const [loading, setLoading] = useState(true);

	// Fetch initial data when authenticated
	useEffect(() => {
		if (!isAuthenticated) {
			setLoading(false);
			return;
		}

		async function fetchData() {
			try {
				const headers = getAuthHeaders(token);
				const [playersRes, matchesRes] = await Promise.all([
					fetch("/api/players", { headers }),
					fetch("/api/matches", { headers }),
				]);
				const players: ApiPlayer[] = await playersRes.json();
				const matches: ApiMatch[] = await matchesRes.json();
				setState({
					players: players.map(transformPlayer),
					matches: matches.map(transformMatch),
				});
			} catch (e) {
				console.error("Failed to fetch data:", e);
			} finally {
				setLoading(false);
			}
		}
		fetchData();
	}, [isAuthenticated, token]);

	const refreshData = useCallback(async () => {
		try {
			const headers = getAuthHeaders(token);
			const [playersRes, matchesRes] = await Promise.all([
				fetch("/api/players", { headers }),
				fetch("/api/matches", { headers }),
			]);
			const players: ApiPlayer[] = await playersRes.json();
			const matches: ApiMatch[] = await matchesRes.json();
			setState({
				players: players.map(transformPlayer),
				matches: matches.map(transformMatch),
			});
		} catch (e) {
			console.error("Failed to refresh data:", e);
		}
	}, [token]);

	const addPlayer = useCallback(
		async (name: string) => {
			const trimmedName = name.trim();
			if (!trimmedName) return;

			try {
				const res = await fetch("/api/players", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						...getAuthHeaders(token),
					},
					body: JSON.stringify({ name: trimmedName }),
				});
				if (res.ok) {
					const player: ApiPlayer = await res.json();
					setState((prev) => ({
						...prev,
						players: [...prev.players, transformPlayer(player)],
					}));
				}
			} catch (e) {
				console.error("Failed to add player:", e);
			}
		},
		[token],
	);

	const removePlayer = useCallback(
		async (playerId: string) => {
			try {
				await fetch(`/api/players/${playerId}`, {
					method: "DELETE",
					headers: getAuthHeaders(token),
				});
				setState((prev) => ({
					...prev,
					players: prev.players.filter((p) => p.id !== playerId),
				}));
			} catch (e) {
				console.error("Failed to remove player:", e);
			}
		},
		[token],
	);

	const recordMatch = useCallback(
		async (winnerId: string, loserId: string) => {
			if (winnerId === loserId) return;

			try {
				const res = await fetch("/api/matches", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						...getAuthHeaders(token),
					},
					body: JSON.stringify({ winnerId, loserId }),
				});
				if (res.ok) {
					// Refresh all data to get updated ELOs
					await refreshData();
				}
			} catch (e) {
				console.error("Failed to record match:", e);
			}
		},
		[refreshData, token],
	);

	const undoLastMatch = useCallback(async () => {
		try {
			const res = await fetch("/api/matches/undo", {
				method: "POST",
				headers: getAuthHeaders(token),
			});
			if (res.ok) {
				await refreshData();
			}
		} catch (e) {
			console.error("Failed to undo match:", e);
		}
	}, [refreshData, token]);

	const resetAll = useCallback(async () => {
		try {
			await fetch("/api/reset", {
				method: "POST",
				headers: getAuthHeaders(token),
			});
			setState({ players: [], matches: [] });
		} catch (e) {
			console.error("Failed to reset:", e);
		}
	}, [token]);

	const sortedPlayers = [...state.players].sort((a, b) => b.elo - a.elo);

	return {
		players: state.players,
		sortedPlayers,
		matches: state.matches,
		loading,
		addPlayer,
		removePlayer,
		recordMatch,
		undoLastMatch,
		resetAll,
	};
}
